/**
 * Templates de minuta, CCG e mensagem.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams } from './comum';

import { api, requestBlob, requestPage } from '../client';

/**
 * Tipo do modelo. Apolice e laudo de emissao vem do documento da apolice do OnPoint:
 * o modelo e escrito e conferido no vocabulario certo, e a geracao na emissao e do
 * pacote do documento da apolice. A CCG da operacao (`ccg`) e a CCG do tomador
 * (`ccg_tomador`) sao modelos diferentes, cada um com o vocabulario do que preenche
 * (ADR 0077).
 */
export type TemplateKind =
  'minuta' | 'ccg' | 'ccg_tomador' | 'email' | 'whatsapp' | 'apolice' | 'laudo_emissao';

/** Onde o modelo e usado. Cada tipo de modelo usa o contexto de mesmo sentido. */
export type TemplateContext =
  'minuta' | 'ccg' | 'ccg_tomador' | 'apolice' | 'laudo' | 'email' | 'objeto';

/** Contexto das variaveis de cada tipo de modelo, o mesmo que o servidor confere. */
export const contextoDoTipo: Record<TemplateKind, TemplateContext> = {
  minuta: 'minuta',
  ccg: 'ccg',
  ccg_tomador: 'ccg_tomador',
  apolice: 'apolice',
  laudo_emissao: 'laudo',
  email: 'email',
  whatsapp: 'email',
};

/** Tipos que produzem documento e por isso aceitam o modelo em Word. */
export const tiposDeDocumento: TemplateKind[] = [
  'minuta',
  'ccg',
  'ccg_tomador',
  'apolice',
  'laudo_emissao',
];

/** Forma de entrega do documento: o Word gerado do modelo ou o PDF dele. */
export type DeliveryFormat = 'docx' | 'pdf';
export type TemplateStatus = 'ativo' | 'inativo';

/**
 * Forma do modelo.
 *
 * O de texto guarda o corpo no proprio registro e serve a e-mail e mensagem. O do
 * Word guarda um arquivo no armazenamento e serve ao contrato, que tem margem,
 * cabecalho, rodape, tabela, imagem e quebra de pagina.
 *
 * O formato e do modelo, e nao da versao: ele nao muda depois de criado.
 */
export type TemplateFormat = 'texto' | 'docx';

export interface Template {
  id: string;
  number: number;
  key: string;
  name: string;
  kind: TemplateKind;
  format: TemplateFormat;
  category: string;
  status: TemplateStatus;
  current_version: number;
  created_at: string;
  updated_at: string;
}

export interface TemplateInput {
  key: string;
  name: string;
  kind: TemplateKind;
  format?: TemplateFormat;
  category?: string;
  body?: string;
}

export interface TemplateUpdateInput {
  name: string;
  category?: string;
  status?: TemplateStatus;
}

export interface TemplateVersion {
  id: string;
  template_id: string;
  version: number;
  format: TemplateFormat;
  /** Corpo no modelo de texto; texto extraido do arquivo no modelo do Word. */
  body: string;
  notes: string;
  /** Arquivo publicado. Vazio no modelo de texto. */
  filename: string;
  byte_size: number;
  checksum: string;
  published_label: string;
  published_at: string;
}

/** Endereco temporario para gravar o arquivo do modelo. */
export interface EnvioReservado {
  staging_key: string;
  upload_url: string;
  expires_at: string;
}

/** Endereco temporario de leitura no armazenamento. */
export interface EnderecoTemporario {
  url: string;
}

export interface TemplateVariable {
  name: string;
  description: string;
  /** Parte antes do ponto, para a paleta agrupar. */
  group: string;
  /** Nome da lista quando a variavel e de item repetido. Vazio na simples. */
  list: string;
  /** Texto rico: entra com paragrafos e formatacao. */
  rich: boolean;
  /** Dado pessoal (LGPD). */
  personal_data: boolean;
  /** A plataforma ja tem o dado e preenche na geracao. */
  available: boolean;
  contexts: TemplateContext[];
  /** So a CCG do tomador preenche: vale so no modelo do tipo ccg_tomador. */
  policyholder_ccg_only: boolean;
}

/** Itens de exemplo por lista, para a previa. */
export type ListasDeExemplo = Record<string, Record<string, string>[]>;

export const templates = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Template>(`/api/v1/templates${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<Template>(`/api/v1/templates/${id}`, signal),
  create: (input: TemplateInput) => api.post<Template>('/api/v1/templates', input),
  update: (id: string, input: TemplateUpdateInput) =>
    api.patch<Template>(`/api/v1/templates/${id}`, input),
  remove: (id: string) => api.delete(`/api/v1/templates/${id}`),
  history: (id: string, signal?: AbortSignal) =>
    api.get<TemplateVersion[]>(`/api/v1/templates/${id}/versions`, signal),
  publish: (id: string, body: string, notes: string) =>
    api.post<TemplateVersion>(`/api/v1/templates/${id}/versions`, { body, notes }),

  /** Reserva o endereco para gravar o .docx direto no armazenamento. */
  reservarEnvio: (id: string) =>
    api.post<EnvioReservado>(`/api/v1/templates/${id}/uploads`, {}),

  /** Publica a versao a partir do arquivo ja gravado. */
  publicarArquivo: (
    id: string,
    input: { staging_key: string; filename: string; notes: string },
  ) => api.post<TemplateVersion>(`/api/v1/templates/${id}/versions`, input),

  baixarVersao: (id: string, version: number) =>
    api.get<EnderecoTemporario>(`/api/v1/templates/${id}/versions/${version}/download`),

  /**
   * Vocabulario do contexto. Sem contexto, vem tudo, com os contextos de cada um.
   */
  variables: (context?: TemplateContext, signal?: AbortSignal) =>
    api.get<TemplateVariable[]>(
      `/api/v1/template-variables${context ? `?context=${context}` : ''}`,
      signal,
    ),

  /**
   * Previa do modelo em Word preenchido com exemplos, sem gravar nada. O arquivo
   * volta no corpo da resposta, em Word ou em PDF.
   */
  previa: (
    id: string,
    version: number,
    input: {
      valores?: Record<string, string>;
      listas?: ListasDeExemplo;
      format?: DeliveryFormat;
    } = {},
  ) =>
    requestBlob(`/api/v1/templates/${id}/versions/${version}/preview`, {
      method: 'POST',
      body: input,
    }),
};

// --- Vinculo do modelo do documento da apolice (P-MIN-2) -------------------------

export type TemplateBindingMovement =
  'emissao' | 'endosso' | 'cancelamento' | 'renovacao';

export type TemplateBindingProofStatus =
  'pendente' | 'gerada' | 'aprovada' | 'reprovada' | 'falhou';

export interface TemplateBindingProof {
  status: TemplateBindingProofStatus;
  template_version: number | null;
  filename: string | null;
  byte_size: number | null;
  failure: string;
  generated_at: string | null;
  generated_label: string;
  decided_at: string | null;
  decided_label: string;
  notes: string;
  /** A prova aprovada vale para a versao do modelo em uso. */
  valid: boolean;
  /** A prova foi feita com outra versao do modelo. */
  outdated: boolean;
  url: string | null;
  expires_at: string | null;
}

export interface TemplateBinding {
  id: string;
  template_id: string;
  template_name: string;
  template_key: string;
  current_version: number;
  insurer_id: string | null;
  product_id: string | null;
  modality_group_id: string | null;
  modality_id: string | null;
  movement: TemplateBindingMovement;
  status: 'ativo' | 'inativo';
  notes: string;
  proof: TemplateBindingProof;
  created_label: string;
  updated_label: string;
  created_at: string;
  updated_at: string;
  /** Uso do modelo vinculado: documento da apolice ou minuta da cotacao (P-MIN-5). */
  document_kind: TemplateBindingDocumentKind;
}

/** Uso do vinculo: o documento da apolice ou a minuta da cotacao, antes da emissao. */
export type TemplateBindingDocumentKind = 'apolice' | 'minuta';

export interface TemplateBindingInput {
  template_id: string;
  insurer_id?: string | null;
  product_id?: string | null;
  modality_group_id?: string | null;
  modality_id?: string | null;
  movement?: TemplateBindingMovement;
  notes?: string;
  document_kind?: TemplateBindingDocumentKind;
}

export interface PolicyDocumentSettings {
  broker_susep_code_mode: 'aviso' | 'recusar';
  locked_credit_groups: string[];
  credit_groups: string[];
  updated_label?: string;
  updated_at?: string | null;
}

export const templateBindings = {
  list: (
    params: {
      status?: string;
      movement?: TemplateBindingMovement;
      document_kind?: TemplateBindingDocumentKind;
    } = {},
    signal?: AbortSignal,
  ) =>
    api.get<TemplateBinding[]>(
      `/api/v1/document-template-bindings${query(params)}`,
      signal,
    ),
  create: (input: TemplateBindingInput) =>
    api.post<TemplateBinding>('/api/v1/document-template-bindings', input),
  update: (
    id: string,
    input: { template_id?: string; status?: 'ativo' | 'inativo'; notes?: string },
  ) => api.patch<TemplateBinding>(`/api/v1/document-template-bindings/${id}`, input),
  proof: (id: string, action: 'gerar' | 'aprovar' | 'reprovar', notes = '') =>
    api.post<TemplateBinding>(`/api/v1/document-template-bindings/${id}/proof`, {
      action,
      notes,
    }),
  settings: (signal?: AbortSignal) =>
    api.get<PolicyDocumentSettings>('/api/v1/policy-document-settings', signal),
  saveSettings: (input: {
    broker_susep_code_mode?: 'aviso' | 'recusar';
    locked_credit_groups?: string[];
  }) => api.put<PolicyDocumentSettings>('/api/v1/policy-document-settings', input),
};

// --- Carga dos textos da seguradora (P-MIN-5) -----------------------------------

export type TextLoadKind =
  'nome_cobertura' | 'objeto' | 'condicao' | 'clausula_particular' | 'email';

export type TextLoadResult = 'novo' | 'alterado' | 'igual' | 'erro';

export interface TextLoadTag {
  label: string;
  tag_key: string;
  input_kind?: string;
}

/** Item do pacote, como o arquivo de carga traz. */
export interface TextLoadItemInput {
  kind: TextLoadKind;
  modality_id?: string | null;
  modality_name?: string;
  group_name?: string;
  product_name?: string;
  coverage_id?: string | null;
  coverage_name?: string;
  purpose?: 'cotacao' | 'impressao_apolice';
  condition_kind?: 'condicao_geral' | 'condicoes_especiais' | 'criterios_enquadramento';
  name?: string;
  key?: string;
  category?: string;
  body?: string;
  insurer_reference?: string;
  tags?: TextLoadTag[];
}

export interface TextLoadInput {
  label?: string;
  items: TextLoadItemInput[];
}

export interface TextLoadItem {
  position: number;
  kind: TextLoadKind;
  target: string;
  result: TextLoadResult;
  messages: string[];
  modality_id: string | null;
  coverage_id: string | null;
  template_id: string | null;
  version: number | null;
  body_sha256: string;
  body_characters: number;
}

export interface TextLoadCheck {
  valid: boolean;
  package_sha256: string;
  items: TextLoadItem[];
}

export interface TextLoad {
  id: string;
  number: number;
  label: string;
  package_sha256: string;
  item_count: number;
  created_count: number;
  changed_count: number;
  unchanged_count: number;
  created_label: string;
  created_at: string;
  items?: TextLoadItem[];
}

export const textLoads = {
  list: (params: { limit?: number; offset?: number } = {}, signal?: AbortSignal) =>
    requestPage<TextLoad>(`/api/v1/text-loads${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<TextLoad>(`/api/v1/text-loads/${id}`, signal),
  check: (input: TextLoadInput) =>
    api.post<TextLoadCheck>('/api/v1/text-loads/check', input),
  apply: (input: TextLoadInput) => api.post<TextLoad>('/api/v1/text-loads', input),
};
